import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import './GameDetails.css';
import FeedbackButton from '../components/FeedbackButton';
import Navbar from '../components/Navbar';
import LoadingLogo from '../components/LoadingLogo';
import authClient from '../utils/authClient';
import { Seo } from '../components/Seo';

const parseJson = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch (_error) { return fallback; }
};

const GameDetails = ({ previewMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewing, setReviewing] = useState(false);
  
  // State for manuals in new format
  const [manuals, setManuals] = useState([]);
  const [activeManualIndex, setActiveManualIndex] = useState(0);

  // Add to the existing useState section:
  const [gameTags, setGameTags] = useState([]);

  // Favorites management functions
  const getFavoritesFromStorage = () => {
    try {
      const stored = localStorage.getItem('rgm_favorite_games');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return [];
    }
  };

  const saveFavoritesToStorage = (favoriteIds) => {
    try {
      localStorage.setItem('rgm_favorite_games', JSON.stringify(favoriteIds));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  };

  const [favoriteGameIds, setFavoriteGameIds] = useState(getFavoritesFromStorage);

  const toggleFavorite = useCallback((gameId) => {
    setFavoriteGameIds(prevIds => {
      const newIds = prevIds.includes(gameId) 
        ? prevIds.filter(id => id !== gameId)
        : [...prevIds, gameId];
      
      saveFavoritesToStorage(newIds);
      return newIds;
    });
  }, []);

  const isFavorite = useCallback((gameId) => {
    return favoriteGameIds.includes(gameId);
  }, [favoriteGameIds]);

  useEffect(() => {
    const objectUrls = [];
    const fetchGameDetails = async () => {
      try {
        setLoading(true);
        setManuals([]);
        setGameTags([]);
        if (previewMode && !authClient.isAuthenticated()) {
          navigate('/login', { state: { from: { pathname: `/manage/pending-game/${id}` } } });
          return;
        }
        const gameRes = await apiClient.get(previewMode ? `/visitor-game-submissions/${id}` : `/games/${id}`);
        let gameData = gameRes.data;

        if (previewMode) {
          const cover = parseJson(gameData.cover_file, null);
          if (cover?.filename) {
            const coverResponse = await apiClient.get(
              `/visitor-game-submissions/${id}/files/${encodeURIComponent(cover.filename)}`,
              { responseType: 'blob' }
            );
            const coverObjectUrl = URL.createObjectURL(coverResponse.data);
            objectUrls.push(coverObjectUrl);
            gameData = { ...gameData, cover: coverObjectUrl };
          }
          const pendingManuals = parseJson(gameData.manual_files, []);
          const previewManuals = [];
          for (const manual of pendingManuals) {
            try {
              const manualResponse = await apiClient.get(
                `/visitor-game-submissions/${id}/files/${encodeURIComponent(manual.filename)}`,
                { responseType: 'blob' }
              );
              const manualUrl = URL.createObjectURL(manualResponse.data);
              objectUrls.push(manualUrl);
              previewManuals.push({ name: manual.name, path: manualUrl });
            } catch (_manualError) {
              previewManuals.push({ name: manual.name, path: '' });
            }
          }
          gameData = { ...gameData, manual: JSON.stringify(previewManuals) };
          setReviewComment(gameData.review_comment || '');
        }
        setGame(gameData);
        
        // Process manuals
        if (gameData.manual) {
          try {
            // Try to parse as JSON
            const parsedManuals = JSON.parse(gameData.manual);
            if (Array.isArray(parsedManuals) && parsedManuals.length > 0) {
              setManuals(parsedManuals);
            } else {
              console.warn('Manual data is not in expected array format');
              // Handle legacy format (fallback)
              if (typeof gameData.manual === 'string' && gameData.manual.startsWith('/')) {
                setManuals([{ name: 'Manual', path: gameData.manual }]);
              }
            }
          } catch (e) {
            console.error('Error parsing manual JSON, treating as legacy format:', e);
            // Handle legacy format (single string path)
            if (typeof gameData.manual === 'string' && gameData.manual.startsWith('/')) {
              setManuals([{ name: 'Manual', path: gameData.manual }]);
            }
          }
        }
        
        const platformsRes = await apiClient.get('/platforms');
        const platform = platformsRes.data.find(p => p.id === parseInt(gameData.platform));
        setPlatform(platform);

        // Fetch game tags
        try {
          if (previewMode) {
            const selectedIds = parseJson(gameData.tag_ids, []).map(Number);
            const tagsRes = await apiClient.get('/tags');
            setGameTags(tagsRes.data.filter((tag) => selectedIds.includes(Number(tag.id))));
          } else {
            const tagsRes = await apiClient.get(`/games/${id}/tags`);
            setGameTags(tagsRes.data);
          }
        } catch (tagErr) {
          console.error('Error fetching game tags:', tagErr);
          // Don't set any error state, just log it
        }
      } catch (err) {
        console.error('Error fetching game details:', err);
        setError('无法载入游戏详情，请稍后重试。');
      } finally {
        setLoading(false);
      }
    };

    fetchGameDetails();
    return () => objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [id, navigate, previewMode]);

  const handleReview = async (action) => {
    const label = action === 'approve' ? '批准并发布' : '拒绝';
    if (!window.confirm(`确定要${label}这个访客提交吗？`)) return;
    setReviewing(true);
    try {
      const response = await apiClient.post(`/visitor-game-submissions/${id}/${action}`, { reviewComment });
      if (action === 'approve') navigate(`/game-details/${response.data.gameId}`);
      else navigate('/manage');
    } catch (reviewError) {
      setError(reviewError.message || `${label}失败。`);
      setReviewing(false);
    }
  };

  const openFeedbackModal = (type) => {
    // Navigate directly to feedback page with pre-filled data
    const params = new URLSearchParams({
      new: 'true',
      type: type,
      gameId: game.id,
      gameName: game.titleeng || game.titlechn
    });
    navigate(`/feedback?${params.toString()}`);
  };

  // Handler for changing the active manual
  const handleManualChange = (index) => {
    setActiveManualIndex(index);
  };

  if (loading) {
    return (
      <div className="game-details-wrapper">
        <Navbar />
        <div className="game-details-container">
          <div className="game-loading-container">
            <LoadingLogo />
            <div className="loading-text">加载游戏信息中...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-details-wrapper">
        <Navbar />
        <div className="game-details-container">
          <div className="game-details-header">
            <button onClick={() => navigate(previewMode ? '/manage' : '/games')} className="back-button">
              <span className="back-icon">←</span>返回
            </button>
          </div>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="game-details-wrapper">
        <Navbar />
        <div className="game-details-container">
          <div className="error-message">未找到该游戏</div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-details-wrapper">
      {previewMode && <Seo title="待审核游戏预览 | 怀旧游戏博物馆" description="管理员预览待审核游戏。" robots="noindex, nofollow, noarchive" />}
      <Navbar />
      <div className="game-details-container">
        {previewMode && (
          <div className="pending-real-preview-bar">
            <div>
              <strong>待审核预览</strong>
              <span>此页面使用正式游戏详情布局，仅管理员可见。</span>
              {game.game_application_id && <span>关联游戏申请 #{game.game_application_id}：{game.application_title_cn || game.application_title_en}</span>}
              <span>提交者：{game.contributor_alias} · IP：{game.submitter_ip}</span>
            </div>
            <textarea value={reviewComment} onChange={(event) => setReviewComment(event.target.value)} maxLength="2000" placeholder="馆员审核留言（通过或拒绝后贡献者可见）" />
            <button type="button" className="preview-reject" disabled={reviewing} onClick={() => handleReview('reject')}>拒绝</button>
            <button type="button" className="preview-approve" disabled={reviewing} onClick={() => handleReview('approve')}>批准并发布</button>
          </div>
        )}
        {/* Header with back button and game title */}
        <div className="game-details-header">
          <button onClick={() => navigate(previewMode ? '/manage' : '/games')} className="back-button">
            <span className="back-icon">←</span>返回
          </button>
          <div className="game-details-title">
            {game.titleeng ? (
              <h1 className="game-title-eng" style={{textAlign: 'center'}}>{game.titleeng}</h1>
            ) : game.titlechn ? (
              <h1 className="game-title-chn" style={{textAlign: 'center'}}>{game.titlechn}</h1>
            ) : (
              <h1 className="game-title-eng" style={{textAlign: 'center'}}>未命名游戏</h1>
            )}
            {game.titlechn && game.titleeng && <h2 className="game-title-chn" style={{textAlign: 'center'}}>{game.titlechn}</h2>}
          </div>
        </div>

        {/* Main content */}
        <div className="game-details-content">
          <div className="game-details-main">
            {/* Left column - Cover and metadata */}
            <div className="game-details-left">
              <div className="game-cover-container">
                <div className="game-details-cover">
                  {game.cover ? (
                    <img
                      src={game.cover.startsWith('blob:') ? game.cover : `${API_BASE_URL}${game.cover}`}
                      alt={game.titleeng || game.titlechn}
                    />
                  ) : <div className="no-content">暂无封面</div>}
                </div>
              </div>
              
              <div className="game-metadata">
                <div className="metadata-section">
                  <h3 className="metadata-heading">游戏信息</h3>
                  
                  <div className="metadata-item">
                    <span className="metadata-label">平台:</span> 
                    <span className="metadata-value">{platform ? (platform.nameeng || platform.namechn || '未知') : '未知'}</span>
                  </div>
                  
                  <div className="metadata-item">
                    <span className="metadata-label">发行年份:</span> 
                    <span className="metadata-value">{game.year || '未知'}</span>
                  </div>
                  
                  <div className="metadata-item">
                    <span className="metadata-label">开发者:</span> 
                    <span className="metadata-value">{game.dev || '未知'}</span>
                  </div>
                  
                  <div className="metadata-item">
                    <span className="metadata-label">发行商:</span> 
                    <span className="metadata-value">{game.publisher || '未知'}</span>
                  </div>

                  {game.translator && (
                    <div className="metadata-item">
                      <span className="metadata-label">汉化组:</span> 
                      <span className="metadata-value">{game.translator}</span>
                    </div>
                  )}

                  {(game.contributor_alias || game.creatorDisplayName) && (
                    <div className="metadata-item creator-item">
                      <span className="metadata-label">收录者:</span> 
                      <span className="metadata-value creator-name">{game.contributor_alias || game.creatorDisplayName}</span>
                    </div>
                  )}
                </div>

                {gameTags.length > 0 && (
                  <div className="popup-tags">
                    <div className="popup-tags-container">
                      {gameTags.map(tag => (
                        <span 
                          key={tag.id} 
                          className="popup-tag"
                          style={{ borderColor: tag.color || '#6c757d' }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="action-buttons">
                  <button 
                    className={`game-details-favorite-button ${isFavorite(game.id) ? 'favorited' : ''}`}
                    onClick={() => !previewMode && toggleFavorite(game.id)}
                    disabled={previewMode}
                    title={isFavorite(game.id) ? '取消收藏' : '添加到收藏'}
                  >
                    <span className="favorite-icon">
                      {isFavorite(game.id) ? '★' : '☆'}
                    </span>
                    <span className="favorite-text">
                      {previewMode ? '审核通过后可收藏' : isFavorite(game.id) ? '已收藏' : '收藏'}
                    </span>
                  </button>
                  <button 
                    className="game-details-play-button"
                    onClick={() => navigate(previewMode ? `/manage/pending-game/${id}/play` : `/game/${game.id}`)}
                  >
                    {previewMode ? '测试游玩' : '立即游玩'}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Right column - Description and manual */}
            <div className="game-details-right">
              <div className="game-description">
                <h3 className="section-heading">游戏简介</h3>
                <div className="description-content">
                  {game.desc ? (
                    <p>{game.desc}</p>
                  ) : (
                    <p className="no-content">暂无游戏简介</p>
                  )}
                </div>
              </div>
              
              <div className="game-manual">
                <h3 className="section-heading">游戏手册</h3>
                {manuals.length > 0 ? (
                  <div className="manual-section">
                    {/* Manual tab selector */}
                    {manuals.length > 1 && (
                      <div className="manual-selector">
                        <div className="manual-tabs">
                          {manuals.map((manual, index) => (
                            <button
                              key={index}
                              className={`manual-tab ${index === activeManualIndex ? 'active' : ''}`}
                              onClick={() => handleManualChange(index)}
                            >
                              {manual.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Manual display */}
                    <div className="manual-container">
                      <iframe 
                        src={`/flipbook.html?pdf=${encodeURIComponent(manuals[activeManualIndex].path?.startsWith('blob:') ? manuals[activeManualIndex].path : `${API_BASE_URL}${manuals[activeManualIndex].path}`)}`}
                        title={manuals[activeManualIndex].name} 
                        className="manual-frame"
                        allowFullScreen={true}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="no-manual">
                    <p className="no-content">暂无游戏手册</p>
                    {/* Feedback Button for requesting/submitting a manual */}
                    {!previewMode && <button
                      className="game-details-feedback-button manual-request-button"
                      onClick={() => openFeedbackModal('manual_request')}
                    >
                      提交/请求手册
                    </button>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add floating feedback button to match other pages */}
        {!previewMode && <FeedbackButton
          type="inaccurate" 
          gameId={game.id} 
          gameName={game.titleeng || game.titlechn} 
          position="bottom-right"
        />}
      </div>
    </div>
  );
};

export default GameDetails;
